import { attr } from '@ember-data/model';
import { cached, tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import BaseModel from 'volta/models/base-model';
import { collectionCommand } from 'volta/utils/api';

import type { IResourceName } from 'volta/models/base-model';
import type { TAcquisitionCode } from 'volta/models/constants/acquisition-codes';
import type { TPlanningMethodology, TReorderStrategy } from 'volta/models/types/planning-settings';
import type { ICmdResult, IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import type { TPeriod } from 'volta/utils/date-utils';
import moment from 'moment-timezone';

export const ExternalForecastResourceName: IResourceName = {
  singular: 'externalForecast',
  plural: 'externalForecasts'
};

export const VOLTA_FINAL_SALES = 'VOLTA_FINAL_SALES' as const;

export const VOLTA_PREVIOUS_FINAL_SALES = 'VOLTA_PREVIOUS_FINAL_SALES' as const;

export default class ExternalForecast extends BaseModel {
  // Commands
  static UpdateForecastGroup = collectionCommand<
    IUpdateForecastGroupCmd,
    ExternalForecast[] & { meta: { totalCompletion: number } },
    typeof ExternalForecast
  >('UpdateForecastGroup');

  static CopySeriesToFinal = collectionCommand<
    ICopySeriesToFinalCmd,
    ExternalForecast[] & { meta: { totalCompletion: number } },
    typeof ExternalForecast
  >('CopySeriesToFinal');

  static ValidateForecastGroup = collectionCommand<
    IValidateForecastGroupCmd,
    ICmdResult<{ totalCompletion: number; validatedCount: number }>,
    typeof ExternalForecast
  >('ValidateForecastGroup');

  static RollbackForecastGroup = collectionCommand<
    IRollbackForecastGroupCmd,
    ExternalForecast[] & { meta: { totalCompletion: number; rollbackCount: number } },
    typeof ExternalForecast
  >('RollbackForecastGroup');

  // Attributes

  @attr('string')
  declare forecastProjectId: string;

  @attr()
  declare fields: TForecastGroupFields;

  @attr()
  declare demandQty: Record<string, Record<string, number>>;

  @attr('number')
  declare forecastsCount: number;

  @attr('number')
  declare itemsCount: number;

  @attr('number')
  declare validatedCount: number;

  @attr('boolean')
  declare isDirty: boolean;

  @attr()
  skuInfo?: IExternalForecastSkuInfo;

  // Tracked Properties

  @tracked isFetchingChildren: boolean = false;
  @tracked children: ExternalForecast[] = [];

  // Getters

  @cached
  get demandQtySums() {
    return Object.entries(this.demandQty).reduce(
      (acc, [key, series]) => {
        acc[key] = Object.values(series).reduce((sum, qty) => sum + qty, 0);
        return acc;
      },
      {} as Record<string, number>
    );
  }

  get minForecastDate() {
    const firstForecastDate = Object.values(this.demandQty)
      .flatMap((series) => Object.keys(series))
      .sort()[0];
    return moment.min(moment.utc(firstForecastDate), moment.utc());
  }

  get validationCompletion() {
    return this.forecastsCount === 0 ? 0 : this.validatedCount / this.forecastsCount;
  }

  get sortedFieldKeys() {
    return Object.keys(this.fields).sort();
  }

  getCurrentGroupBy(groupBy: TExternalForecastField[][], period?: TPeriod) {
    return groupBy
      .map((gg) => {
        return gg
          .map((g) => {
            return this.fields[g] ? g : undefined;
          })
          .filter(Boolean)
          .join('|');
      })
      .filter((a) => a.length > 0)
      .concat(period ? [`period.${period}`] : []);
  }

  updateCopiedSeries(seriesToCopy: Record<string, number>, copiedCount: number) {
    this.validatedCount = 0;
    this.forecastsCount = copiedCount;
    this.demandQty = {
      ...this.demandQty,
      [VOLTA_FINAL_SALES]: seriesToCopy
    };
  }

  [Type] = 'external-forecast' as const;
}

export interface IExternalForecastSkuInfo {
  skuId: string;
  partCode: string;
  description?: string;
  acqCode: TAcquisitionCode;
  methodology?: TPlanningMethodology;
  reorderStrategy?: TReorderStrategy;
  isProtected?: boolean;
  partUnitMeasure: string;
  warehouseId: string;
  warehouseCode: string;
  warehouseDescription?: string;
}

export type TExternalForecastField =
  | 'acqCode'
  | 'buyer'
  | 'deliveryWorkshop'
  | 'methodology'
  | 'orderType'
  | 'part'
  | 'partType'
  | 'partUnitMeasure'
  | `period.${TPeriod}`
  | 'planner'
  | 'reorderStrategy'
  | 'sku'
  | 'sku.abcClass'
  | 'sku.xyzClass'
  | 'sku.leadTimeCategory'
  | 'sku.varCategory'
  | `sku.customFields.${string}`
  | `source.customFields.${string}`
  | 'source'
  | 'supplier'
  | 'warehouse'
  | 'workshop';

export type TExternalForecastSubGroup = Extract<TExternalForecastField, 'source' | 'sku'>;

export type TForecastGroupField = {
  value?: string;
  id?: string;
  description?: string;
};

export type TForecastGroupFields = Partial<Record<TExternalForecastField, TForecastGroupField>>;

// Commands

export interface IUpdateForecastGroupCmd {
  fpHeaderId: string;
  forecastProjectId: string;
  forecastType?: string;
  groupId: string;
  groupBy: string[];
  forecastDate: string;
  groupFields: TForecastGroupFields;
  filters?: IJsonApiQuery['filter'];
  oldAggQty: number;
  newAggQty: number;
}

export type TCopyMode = 'replace' | 'cumulative' | 'overwrite' | 'deductive';

export interface ICopySeriesToFinalCmd {
  fpHeaderId: string;
  finalForecastType?: string;
  forecastProjectId: string;
  forecastTypeToCopy: string;
  groupBy: string[];
  groupFields: TForecastGroupFields;
  copyMode: TCopyMode;
  frequency: TPeriod;
  excludedDates: string[];
  filters?: IJsonApiQuery['filter'];
}

export interface IValidateForecastGroupCmd {
  fpHeaderId: string;
  forecastProjectId: string;
  groupFields: TForecastGroupFields;
  groupBy: string[];
  filters?: IJsonApiQuery['filter'];
}

export interface IRollbackForecastGroupCmd {
  fpHeaderId: string;
  forecastProjectId: string;
  groupFields: TForecastGroupFields;
  groupBy: string[];
  frequency: TPeriod;
  filters?: IJsonApiQuery['filter'];
}

// Queries

export type TForecastSeriesType = 'manual' | 'demand' | 'supply' | 'stats' | 'automl' | 'stock';

export type TReferenceTimeSeries =
  | 'VOLTA_REF_TX_Y0'
  | 'VOLTA_REF_TX_Y1'
  | 'VOLTA_REF_TX_Y2'
  | 'VOLTA_REF_TX_SUPPLY_Y0'
  | 'VOLTA_REF_TX_SUPPLY_Y1'
  | 'VOLTA_REF_TX_SUPPLY_Y2'
  | 'VOLTA_REF_OPEN_DEMAND'
  | 'VOLTA_REF_OPEN_SUPPLY'
  | 'VOLTA_REF_STOCK_PL'
  | 'VOLTA_REF_STOCK_EX';

export interface IGetReferenceTsRequest {
  forecastProjectId: string;
  groupFields: ExternalForecast['fields'];
  groupBy: string[];
  frequency: TPeriod;
  startDate: string;
  series: TForecastSeriesType[];
  filters?: IJsonApiQuery['filter'];
}

export type IGetReferenceTsResponse = Partial<Record<TReferenceTimeSeries, number[]>> & {
  forecastDate: string[];
};

export type IGetReferenceTsAsDict = Partial<Record<TReferenceTimeSeries, Record<string, number>>>;
